import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import axios from 'axios';
import { saveAs } from 'file-saver';

import Card from 'components/Layout/Card';
import Profile from 'components/Layout/Profile';
import Button from 'components/form/Button';
import ActionForm from '../../Common/ActionForm';
import RequestVerificationModal from '../../Common/RequestVerificationModal';
import VerifyOtpModel from 'pages/Common/RequestRaisedAndReceived/Common/VerifyOtpModel';

import {
  generatePreSignedUrl,
  getDataFromLocalStorage,
  getEventDate,
  getFormDataArrayObject,
  getStatus,
  getStatusColor,
  objectToFormData,
  handleFileDownload,
} from 'utils/helpers';
import { eventAbstarctPath, requestDataPath, icons, paymentAbstractPath } from 'utils/constants';
import {
  fetchParticipantRequestDetails,
  handleSendParticipantOtp,
  handleVerifyParticipantOtpRequest,
  showSuccess,
  throwError,
} from 'store/slices';
import Badge from 'react-bootstrap/Badge';
import './ParticipantsRequest.scss';
import Loader from 'components/Layout/Loader';
// --- Helper Components ---
import DetailItem from 'components/Layout/DetailItem';

// --- Main Component ---

const ParticipantRequestDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { requestId } = useParams();

  // State
  const [participantData, setParticipantData] = useState({});
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState(false);
  const [otpVerification, setOtpVerification] = useState(false);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [otpData, setOtpData] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const formikRef = useRef(null);

  const getParticipantRequestDetails = async () => {
    setIsLoading(true);
    const response = await dispatch(fetchParticipantRequestDetails(requestId));
    setParticipantData(response.data);
    setIsLoading(false);
  };

  // Fetch participant details
  useEffect(() => {
    getParticipantRequestDetails();
    // eslint-disable-next-line
  }, [requestId]);

  // Destructure participantData
  const {
    paper_abstract,
    user_member_id,
    user_name,
    user_email_id,
    user_country_code,
    user_phone_no,
    user_country,
    user_membership_plan,
    user_type_of_participation,
    paper_title,
    abstract_path,
    payment_proof,
    paper_keywords,
    presentation_type,
    submission_type,
    event_name,
    event_type,
    event_start_date,
    event_end_date,
    event_city,
    event_county,
    resource_name,
    resource_member_id,
    resource_email_id,
    resource_submitted_at,
    abstractPathFileName,
    paymentProofFileName,
    author_name,
    co_authors,
    admin_name,
    admin_member_id,
    admin_email_id,
    admin_document,
    admin_status,
    admin_comment,
    profile_photo_path,
    sadmin_name,
    sadmin_member_id,
    sadmin_email_id,
    sadmin_document,
    sadmin_status,
    sadmin_comment,
    is_new_registered,
  } = participantData;

  // User type and status
  const user_type = getDataFromLocalStorage('user_type');
  const isAdmin = user_type === '0';
  const isSuperAdmin = user_type === '1';
  const isActionTakenByAdmin = admin_status !== 0;
  const isActionTakenBySuperAdmin = sadmin_status !== 0;

  // Formik setup
  const initialValues = useMemo(
    () => ({
      comment: '',
      document: '',
    }),
    []
  );
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        comment: Yup.string(),
        document: Yup.mixed(),
      }),
    []
  );

  // Abstract text processing
  const maxWords = 50;
  const abstractText = useMemo(
    () =>
      paper_abstract
        ?.replace(/<\/?p>/g, '')
        ?.replace(/<\/?span[^>]*>/g, '')
        ?.trim(),
    [paper_abstract]
  );
  const abstractWords = useMemo(
    () => abstractText?.split(' ')?.filter(Boolean),
    [abstractText]
  );
  const toggleReadMore = () => setIsExpanded((prev) => !prev);

  // File download handler
  // const handleFileDownload = async (e, fileName,filePath) => {
  //   e.preventDefault();
  //   if (!fileName) return;
  //   try {
  //     const url = await generatePreSignedUrl(fileName, filePath);
  //     const response = await axios.get(url, { responseType: 'blob' });
  //     const mimeType = response.data.type;
  //     const blob = new Blob([response.data], { type: mimeType });
  //     saveAs(blob, fileName);
  //   } catch (error) {
  //     console.error('Error downloading file:', error);
  //   }
  // };

  // Form actions
  const handleSubmitAction = (reject = false) => {
    setIsRejecting(reject);
    formikRef.current?.handleSubmit();
  };

  const handleReject = async (values, setErrors) => {
    if (!values.comment.trim() && !values.document) {
      setErrors({
        comment: 'Either a comment or a document is required',
        document: 'Either a comment or a document is required',
      });
      return;
    }
    setOtpData({
      comment: values?.comment,
      document: values?.document,
      action: '2', // Reject
    });
    await sendOtpRequest();
  };

  const handleApproveRequest = async (values, setErrors) => {
    setOtpData({
      comment: values?.comment,
      document: values?.document,
      action: '1', // Approve
    });
    if (user_type === '1') {
      await sendOtpRequest();
    } else {
      setIsOpenConfirmationDialog(true);
    }
  };

  const sendOtpRequest = async () => {
    setBtnLoading(true);
    const payload = objectToFormData({ request_ids: requestId });
    const response = await dispatch(handleSendParticipantOtp(payload));
    if (response.status === 200) {
      setShowOtpDialog(true);
      dispatch(showSuccess(response?.message));
      setBtnLoading(false);
    } else {
      dispatch(throwError(response));
      setBtnLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setOtpVerification(true);
    const payload = getFormDataArrayObject({
      request_ids: requestId,
      ...(otp && { otp }),
      document: otpData?.document,
      comment: otpData?.comment,
      request_status: otpData?.action,
      request_data: 1,
    });
    const response = await dispatch(handleVerifyParticipantOtpRequest(payload));
    if (response.status === 200) {
      setShowOtpDialog(false);
      dispatch(showSuccess(response.message));
      setIsOpenConfirmationDialog(false);
      setOtpVerification(false);
      getParticipantRequestDetails();
    } else {
      dispatch(throwError(response));
      setOtpVerification(false);
    }
  };

  // --- Render ---
  return (
    <>
      <div id="participants-request-container">
        {isLoading ? (
          <Card>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '300px' }}
            >
              <Loader size="sm" />
            </div>
          </Card>
        ) : (
          <div className="row">
            {/* Left Profile Card */}
            <div className="col-12 col-sm-12 col-md-5 col-lg-4">
              <Card>
                <div className="d-flex p-2" onClick={() => navigate(-1)}>
                  <img
                    src={icons.leftArrow}
                    alt="back"
                    className="h-21 me-3 pointer"
                  />
                </div>
                <div className="d-flex align-items-center justify-content-center cpt-17">
                  <Profile
                    isRounded
                    isRoundedBorder
                    size="s-150"
                    isS3UserURL
                    url={profile_photo_path}
                    text={user_name}
                  />
                </div>
                {user_name && (
                  <div className="text-20-400-25 text-center cmt-20 color-text-blue">
                    {user_name}{' '}
                    {is_new_registered === '1' && (
                      <Badge className="custom-badge">New</Badge>
                    )}
                  </div>
                )}
                <div className="cmt-30 cps-24 cpb-20">
                  {user_email_id && (
                    <div className="text-14-300-17 color-black-olive d-flex align-items-center gap-2 cmb-16 text-break">
                      <i className="bi bi-envelope" /> {user_email_id}
                    </div>
                  )}
                  {user_phone_no && (
                    <div className="text-14-300-17 color-black-olive d-flex align-items-center gap-2 cmb-16">
                      <i className="bi bi-telephone" /> +{user_country_code}{' '}
                      {user_phone_no}
                    </div>
                  )}
                  {user_country && (
                    <div className="text-14-300-17 color-black-olive d-flex align-items-center gap-2 cmb-16">
                      <i className="bi bi-globe-americas" /> {user_country}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Right Details Card */}
            <div className="col-12 col-sm-12 col-md-7 col-lg-8">
              {/* Profile Details */}
              <Card className="cp-24">
                <div className="text-14-500-18 color-text-blue cmb-10">
                  Profile Details :
                </div>
                <div className="row">
                  {/* <div className="col-md-4 col-sm-6 col-12">
                    <DetailItem label="Profile ID" value={user_member_id} />
                  </div> */}
                  <div className="col-md-4 col-sm-6 col-12">
                    <DetailItem label="MemberShip ID" value={user_member_id} />
                  </div>
                  <div className="col-md-4 col-sm-6 col-12">
                    <DetailItem
                      label="MemberShip Type"
                      value={user_membership_plan}
                    />
                  </div>
                  <div className="col-md-4 col-sm-6 col-12">
                    <DetailItem
                      label="Type of Participant"
                      value={user_type_of_participation}
                    />
                  </div>
                </div>
              </Card>

              {/* Abstract Details */}
              <Card className="cp-24 cmt-10">
                <div>
                  <div className="text-14-500-18 color-text-blue cmb-18">
                    Abstract Details :
                  </div>
                  <div className="text-14-400-18 color-1717">Paper Title</div>
                  <div className="text-14-300-24 color-black-olive cpt-8">
                    {paper_title ? paper_title : '-'}
                  </div>

                  <div className="text-14-400-18 color-1717 cmt-10">
                    Abstract
                  </div>
                  <div className="text-14-300-24 color-black-olive cpt-8 cmb-12">
                    {paper_abstract ?
                      <div className=" text-14-300-24 color-black-olive">
                        <div>
                          {isExpanded ? (
                            <span
                              dangerouslySetInnerHTML={{ __html: paper_abstract }}
                            />
                          ) : (
                            <>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: abstractWords
                                    ?.slice(0, maxWords)
                                    ?.join(' '),
                                }}
                              />
                              {abstractWords?.length > maxWords && (
                                <>
                                  {'... '}
                                  <span
                                    onClick={toggleReadMore}
                                    className="text-14-500-23 color-new-car pointer"
                                  >
                                    Read More
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        {isExpanded && (
                          <span
                            onClick={toggleReadMore}
                            className="text-14-500-23 color-new-car pointer"
                          >
                            Read Less
                          </span>
                        )}
                      </div> : '-'}
                  </div>

                  <div >
                    <div className="row">
                      <div className="col-md-3 col-sm-6 col-12">
                        <DetailItem
                          label="Paper Documents"
                          type="url"
                          fileName={abstract_path}
                          handleFileDownload={handleFileDownload}
                          value={abstractPathFileName}
                          filePath={eventAbstarctPath}
                        />
                      </div>
                      <div className="col-md-3 col-sm-6 col-12">
                        <DetailItem
                          label="Payment Proof"
                          type="url"
                          fileName={payment_proof}
                          handleFileDownload={handleFileDownload}
                          value={payment_proof}
                          filePath={paymentAbstractPath}
                        />
                      </div>
                      <div className="col-md-3 col-sm-6 col-12">
                        <DetailItem label="Keywords" value={paper_keywords} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-3 col-sm-6 col-12">
                        <DetailItem label="Author" value={author_name} />
                      </div>
                      <div className="col-md-3 col-sm-6 col-12">
                        <DetailItem label="Co-Authors" value={co_authors} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-3 col-sm-6 col-12">
                        <DetailItem
                          label="Presentation Type"
                          value={presentation_type}
                        />
                      </div>
                      <div className="col-md-3 col-sm-6 col-12">
                        <DetailItem
                          label="Submission Type"
                          value={submission_type}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Track Details */}
                <div>
                  <div className="text-14-500-18 color-text-blue cmb-10 cmt-18">
                    Track Details :
                  </div>
                  <div className="row">
                    <div className="col-md-12 col-sm-12 col-12">
                      <DetailItem label="Event Name" value={event_name} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4 col-sm-6 col-12">
                      <DetailItem label="Event Type" value={event_type} />
                    </div>
                    <div className="col-md-4 col-sm-6 col-12">
                      <DetailItem
                        label="Event Date"
                        value={getEventDate(event_start_date, event_end_date)}
                      />
                    </div>
                    <div className="col-md-4 col-sm-6 col-12">
                      <DetailItem
                        label="Place"
                        value={`${event_city},${event_county}`}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-3 col-sm-6 col-12">
                      <DetailItem label="Added By" value={resource_name} />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12">
                      <DetailItem label="Id" value={resource_member_id} />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12">
                      <DetailItem label="Email Id" value={resource_email_id} />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12">
                      <DetailItem
                        label="Submitted"
                        value={resource_submitted_at}
                      />
                    </div>
                  </div>

                  {/* Admin/SuperAdmin Action Section */}
                  <>
                    {isActionTakenByAdmin ? (
                      <div>
                        <div className="text-14-500-18 color-text-blue cmb-10">
                          Action Details :
                        </div>
                        <div className="row">
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem label="Admin" value={admin_name} />
                          </div>
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem label="Id" value={admin_member_id} />
                          </div>
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem
                              label="Email Id"
                              value={admin_email_id}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem
                              label="Paper Documents"
                              value={admin_document}
                              type="url"
                              fileName={admin_document}
                              handleFileDownload={handleFileDownload}
                              filePath={requestDataPath}
                            />
                          </div>
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem
                              label="Status"
                              value={getStatus(admin_status, 'request')}
                              labelClassName={getStatusColor(admin_status)}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12 col-sm-12 col-12">
                            <DetailItem
                              label="Admin Comment"
                              value={admin_comment}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      isAdmin && (
                        <ActionForm
                          formikRef={formikRef}
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          isRejecting={isRejecting}
                          handleReject={handleReject}
                          handleApproveRequest={handleApproveRequest}
                          btnLoading={btnLoading}
                        />
                      )
                    )}
                  </>
                  <>
                    {isActionTakenBySuperAdmin ? (
                      <div>
                        <div className="row">
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem
                              label="Super Admin"
                              value={sadmin_name}
                            />
                          </div>
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem label="Id" value={sadmin_member_id} />
                          </div>
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem
                              label="Email Id"
                              value={sadmin_email_id}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-3 col-sm-6 col-12">
                            <DetailItem
                              label="Paper Documents"
                              value={sadmin_document}
                              type="url"
                              fileName={sadmin_document}
                              handleFileDownload={handleFileDownload}
                              filePath={requestDataPath}
                            />
                          </div>
                          <div className="col-md-3 col-sm-3 col-12">
                            <DetailItem
                              label="Status"
                              value={getStatus(sadmin_status, 'request')}
                              labelClassName={getStatusColor(sadmin_status)}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12 col-sm-12 col-12">
                            <DetailItem
                              label="Super Admin Comment"
                              value={sadmin_comment}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      isSuperAdmin && (
                        <ActionForm
                          formikRef={formikRef}
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          isRejecting={isRejecting}
                          handleReject={handleReject}
                          handleApproveRequest={handleApproveRequest}
                          btnLoading={btnLoading}
                        />
                      )
                    )}
                  </>
                </div>
              </Card>

              {/* Action Buttons */}
              {((isAdmin && !isActionTakenByAdmin) ||
                (isSuperAdmin && !isActionTakenBySuperAdmin)) && (
                  <div className="d-flex gap-3 cmt-10">
                    <Button
                      text={
                        user_type === '1' ? 'Approve Request' : 'Send Request'
                      }
                      btnStyle="primary-dark"
                      className="h-37 text-14-500 cps-20 cpe-20"
                      onClick={() => handleSubmitAction(false)}
                    />
                    <Button
                      text="Reject"
                      btnStyle="primary-light-outline"
                      className="h-37 text-14-500 cps-20 cpe-20"
                      onClick={() => handleSubmitAction(true)}
                    />
                    <Button
                      text="Cancel"
                      btnStyle="light-outline"
                      className="h-37 text-14-500 cps-20 cpe-20"
                      onClick={() => navigate(-1)}
                    />
                  </div>
                )}

              {/* Info Message for Admin */}
              {isAdmin && (
                <div className="d-flex align-items-center gap-2 cmt-10">
                  <i className="bi bi-info-circle"></i>
                  <span className="text-14-400-15 color-4141">
                    This send request will be considered as an approval from the
                    admin and will be shared with the super admin.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {isOpenConfirmationDialog && (
        <RequestVerificationModal
          handleConfirmation={() => handleVerifyOtp()}
          onHide={() => setIsOpenConfirmationDialog(false)}
          title="You want to share the approve request to super admin"
          isLoading={otpVerification}
        />
      )}

      {/* OTP Modal */}
      {showOtpDialog && (
        <VerifyOtpModel
          title="Participants Request"
          showApproveDialog={showOtpDialog}
          onHide={() => setShowOtpDialog(false)}
          onVerify={handleVerifyOtp}
          sendOtpRequest={sendOtpRequest}
          otpVerification={otpVerification}
        />
      )}
    </>
  );
};

export default ParticipantRequestDetails;
